// PanelContextMenu.jsx
// Right-click context menu that hangs off every QuestDBPanel. Small, focused:
// Show data (opens a modal), Copy value (nearest data point), Export CSV,
// Export JSON. Portal-rendered so it can escape overflow:hidden ancestors.

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Table as TableIcon, ClipboardCopy, FileDown, Braces } from 'lucide-react';

export default function PanelContextMenu({
  x, y, data, panelTitle, darkMode = true,
  onShowTable, onClose
}) {
  useEffect(() => {
    const onDoc = (e) => {
      // Close on click outside or Escape
      onClose?.();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const T = darkMode ? {
    bg: '#111827', border: '#1f2937', text: '#e5e7eb',
    hover: 'rgba(139,92,246,0.14)', mute: '#94a3b8'
  } : {
    bg: '#ffffff', border: '#e2e8f0', text: '#0f172a',
    hover: 'rgba(124,58,237,0.10)', mute: '#64748b'
  };

  const rows = Array.isArray(data) ? data : [];

  // Columns worth showing/exporting — drop internal underscored keys.
  const cols = rows.length ? Object.keys(rows[0]).filter(k => !k.startsWith('_')) : [];

  const download = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const exportCsv = () => {
    if (!rows.length) return;
    const escape = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [cols.join(','), ...rows.map(r => cols.map(c => escape(r[c])).join(','))];
    const safe = (panelTitle || 'panel').replace(/[^a-z0-9-_]+/gi, '_');
    download(new Blob([lines.join('\n')], { type: 'text/csv' }), `${safe}.csv`);
    onClose?.();
  };

  const exportJson = () => {
    if (!rows.length) return;
    // Strip internal keys for readability
    const clean = rows.map(r => {
      const o = {};
      for (const k of cols) o[k] = r[k];
      return o;
    });
    const safe = (panelTitle || 'panel').replace(/[^a-z0-9-_]+/gi, '_');
    download(new Blob([JSON.stringify(clean, null, 2)], { type: 'application/json' }), `${safe}.json`);
    onClose?.();
  };

  const copyLatestValue = async () => {
    if (!rows.length) return;
    const latest = rows[rows.length - 1];
    // Prefer a numeric column
    const numericKey = cols.find(c => typeof latest[c] === 'number');
    const key = numericKey || cols[0];
    if (!key) return;
    try { await navigator.clipboard.writeText(String(latest[key] ?? '')); } catch { /* noop */ }
    onClose?.();
  };

  // Clamp menu to viewport so it doesn't overflow off-screen
  const menuW = 200, menuH = 176;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const left = Math.min(x, vw - menuW - 8);
  const top  = Math.min(y, vh - menuH - 8);

  const item = (Icon, label, onClick, disabled = false) => (
    <button
      onClick={(e) => { e.stopPropagation(); if (!disabled) onClick(); }}
      onMouseDown={(e) => e.stopPropagation()}
      disabled={disabled}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
        padding: '7px 12px', background: 'transparent', border: 'none',
        color: disabled ? T.mute : T.text, textAlign: 'left',
        fontSize: 12, cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.1s'
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = T.hover; }}
      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
    >
      <Icon size={13} />
      {label}
    </button>
  );

  return createPortal(
    <div
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        position: 'fixed', top, left, width: menuW,
        background: T.bg, border: `1px solid ${T.border}`,
        borderRadius: 8, boxShadow: '0 12px 32px rgba(0,0,0,0.4)',
        padding: '4px 0', zIndex: 10000, overflow: 'hidden'
      }}
    >
      {item(TableIcon, 'Show as table', () => { onShowTable?.(); onClose?.(); }, !rows.length)}
      {item(ClipboardCopy, 'Copy latest value', copyLatestValue, !rows.length)}
      <div style={{ height: 1, background: T.border, margin: '4px 0' }} />
      {item(FileDown, 'Export CSV', exportCsv, !rows.length)}
      {item(Braces, 'Export JSON', exportJson, !rows.length)}
    </div>,
    document.body
  );
}

// ─── Show-as-table modal ─────────────────────────────────────────────────
export function ShowDataModal({ data, panelTitle, darkMode = true, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const T = darkMode ? {
    bg: 'rgba(4,7,14,0.72)', card: '#0f172a', ink: '#e5e7eb',
    mute: '#94a3b8', border: '#1f2937', header: '#111827'
  } : {
    bg: 'rgba(15,23,42,0.35)', card: '#ffffff', ink: '#0f172a',
    mute: '#64748b', border: '#e2e8f0', header: '#f1f5f9'
  };

  const rows = Array.isArray(data) ? data : [];
  const cols = rows.length ? Object.keys(rows[0]).filter(k => !k.startsWith('_')) : [];

  const fmt = (v) => {
    if (v == null) return '—';
    if (typeof v === 'number') return v.toLocaleString();
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
      try { return new Date(v).toLocaleString(); } catch { return v; }
    }
    return String(v);
  };

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: T.bg, zIndex: 9800,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(1000px, 100%)', maxHeight: '80vh',
          background: T.card, borderRadius: 12,
          border: `1px solid ${T.border}`, boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
          display: 'flex', flexDirection: 'column'
        }}
      >
        <div style={{
          padding: '14px 18px', borderBottom: `1px solid ${T.border}`,
          display: 'flex', alignItems: 'center', gap: 10
        }}>
          <div style={{ fontWeight: 700, color: T.ink, fontSize: 14, flex: 1 }}>
            {panelTitle || 'Panel'} · {rows.length} row{rows.length !== 1 ? 's' : ''}
          </div>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', color: T.mute,
            cursor: 'pointer', fontSize: 16, padding: 4
          }}>✕</button>
        </div>
        <div style={{ overflow: 'auto', flex: 1 }}>
          <table style={{
            width: '100%', borderCollapse: 'collapse',
            fontSize: 12, tableLayout: 'auto'
          }}>
            <thead>
              <tr style={{ position: 'sticky', top: 0, background: T.header }}>
                {cols.map(c => (
                  <th key={c} style={{
                    padding: '8px 12px', textAlign: 'left',
                    borderBottom: `1px solid ${T.border}`,
                    color: T.mute, fontWeight: 700, fontSize: 10,
                    textTransform: 'uppercase', letterSpacing: 0.5
                  }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} style={{
                  borderBottom: `1px solid ${T.border}`,
                  background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)'
                }}>
                  {cols.map(c => (
                    <td key={c} style={{
                      padding: '6px 12px', color: T.ink,
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums'
                    }}>{fmt(row[c])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>,
    document.body
  );
}
